import { useState, useEffect } from "react";
import type { TranslateFn } from "../../i18n";
import type { Profile, ProfileMeasurements } from "../types";

import iconChestImg from "../assets/icon-chest.png";
import iconBustImg from "../assets/icon-bust.png";
import iconWaistImg from "../assets/icon-waist.png";
import iconHipsImg from "../assets/icon-hips.png";
import iconShoulderImg from "../assets/icon-shoulder.png";
import iconSleeveImg from "../assets/icon-sleeve.png";
import iconNeckImg from "../assets/icon-neck.png";
import iconInseamImg from "../assets/icon-inseam.png";
import iconThighImg from "../assets/icon-thigh.png";

interface ProfileMeasurementsViewProps {
  profile: Profile;
  isActive: boolean;
  onSelect: () => void;
  onSaveMeasurements: (measurements: ProfileMeasurements, unit?: "cm" | "in") => void;
  onDelete: () => void;
  onBack: () => void;
  onSave: (measurements?: ProfileMeasurements, unit?: "cm" | "in", profileDetails?: { bandSize?: string; cupSize?: string }) => void;
  onSaveBraSize?: (bandSize: string, cupSize: string) => void;
  /** Draft mode is used inside profile creation before the profile exists. */
  draftMode?: boolean;
  primaryLabel?: string;
  /** True while background estimation is computing measurements —
   *  renders a spinner for empty fields instead of the "—" placeholder. */
  isEstimating?: boolean;
  t: TranslateFn;
}

interface FieldDef {
  key: string;
  label: string;
  iconSrc: string;
}

const FIELDS_MEN: FieldDef[] = [
  { key: "chest",              label: "CHEST",    iconSrc: iconChestImg },
  { key: "waist",              label: "WAIST",    iconSrc: iconWaistImg },
  { key: "hips",               label: "HIPS",     iconSrc: iconHipsImg },
  { key: "shoulderWidth",      label: "SHOULDER", iconSrc: iconShoulderImg },
  { key: "sleeveLength",       label: "SLEEVE",   iconSrc: iconSleeveImg },
  { key: "neckCircumference",  label: "NECK",     iconSrc: iconNeckImg },
  { key: "inseam",             label: "INSEAM",   iconSrc: iconInseamImg },
  { key: "thighCircumference", label: "THIGH",    iconSrc: iconThighImg },
];
const FIELDS_WOMEN: FieldDef[] = [
  { key: "bust",               label: "BUST",     iconSrc: iconBustImg },
  { key: "waist",              label: "WAIST",    iconSrc: iconWaistImg },
  { key: "hips",               label: "HIPS",     iconSrc: iconHipsImg },
  { key: "shoulderWidth",      label: "SHOULDER", iconSrc: iconShoulderImg },
  { key: "sleeveLength",       label: "SLEEVE",   iconSrc: iconSleeveImg },
  { key: "neckCircumference",  label: "NECK",     iconSrc: iconNeckImg },
  { key: "inseam",             label: "INSEAM",   iconSrc: iconInseamImg },
  { key: "thighCircumference", label: "THIGH",    iconSrc: iconThighImg },
];

const BAND_OPTIONS_US = ["28","30","32","34","36","38","40","42","44","46","48","50","52","54","56"];
const CUP_OPTIONS_US  = ["AA","A","B","C","D","DD/E","DDD/F","G","H","I","J"];

export function ProfileMeasurementsView({
  profile, isActive, onSelect, onSaveMeasurements, onDelete, onBack, onSave, onSaveBraSize, draftMode = false, primaryLabel, isEstimating = false, t,
}: ProfileMeasurementsViewProps) {
  const fields = profile.gender === "female" ? FIELDS_WOMEN : FIELDS_MEN;
  const measurements = profile.measurements || {};
  const hasMeasurements = Object.keys(measurements).some((k) => (measurements as Record<string, number | undefined>)[k] != null);

  const [editUnit, setEditUnit] = useState<"cm" | "in">((profile.measurementsUnit as "cm" | "in") || "cm");

  // Inline draft — keyed by measurement field
  const [draft, setDraft] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    fields.forEach((f) => {
      const v = (measurements as Record<string, number | undefined>)[f.key];
      init[f.key] = v != null ? String(Math.round(v * 10) / 10) : "";
    });
    return init;
  });

  // Re-sync the draft whenever stored measurements change (background
  // estimation finishing, or active profile switching). Only overwrites
  // empty draft fields so the user's in-progress edits aren't clobbered.
  useEffect(() => {
    setDraft((prev) => {
      const next = { ...prev };
      let changed = false;
      fields.forEach((f) => {
        const v = (measurements as Record<string, number | undefined>)[f.key];
        if (v != null && !prev[f.key]) {
          next[f.key] = String(Math.round(v * 10) / 10);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.measurements, profile.id]);

  const [bandSize, setBandSize] = useState<string>(profile.bandSize || "");
  const [cupSize,  setCupSize]  = useState<string>(profile.cupSize  || "");

  const handleUnitChange = (newUnit: "cm" | "in") => {
    if (newUnit === editUnit) return;
    setDraft((prev) => {
      const next: Record<string, string> = {};
      for (const [key, raw] of Object.entries(prev)) {
        const v = parseFloat(raw);
        if (!isNaN(v) && v > 0) {
          next[key] = newUnit === "in"
            ? String(Math.round((v / 2.54) * 10) / 10)
            : String(Math.round(v * 2.54 * 10) / 10);
        } else {
          next[key] = raw;
        }
      }
      return next;
    });
    setEditUnit(newUnit);
  };

  const handleSave = () => {
    const updated: ProfileMeasurements = { ...(profile.measurements || {}) };
    for (const [key, raw] of Object.entries(draft)) {
      const v = parseFloat(raw);
      if (!isNaN(v) && v > 0) (updated as Record<string, number>)[key] = v;
    }
    onSaveMeasurements(updated, editUnit);
    if (profile.gender === "female" && onSaveBraSize && (bandSize || cupSize)) {
      onSaveBraSize(bandSize, cupSize);
    }
    onSave(updated, editUnit, profile.gender === "female" ? { bandSize, cupSize } : undefined);
  };

  return (
    <div className="ps-pmv-root">
      {/* Hero */}
      <div className="ps-pmv-hero">
        <div className="ps-pmv-hero-eyebrow">
          {draftMode ? t("REVIEW PROFILE") : isActive ? t("ACTIVE PROFILE") : profile.gender === "female" ? t("WOMEN'S FIT") : t("MEN'S FIT")}
        </div>
        <h2 className="ps-pmv-hero-name">{profile.name}</h2>
      </div>

      <div className="ps-pmv-data">
        <div className="ps-pmv-section">
          <div className="ps-pmv-section-head">
            <div className="ps-pmv-section-title-row">
              <div className="ps-pmv-section-title">{t("BODY MEASUREMENTS")}</div>
              {/* Unit toggle inline with section title */}
              <div className="ps-cpw-pill-group" style={{ marginLeft: "auto" }}>
                <button type="button"
                  className={`ps-cpw-pill${editUnit === "cm" ? " ps-active" : ""}`}
                  onClick={() => handleUnitChange("cm")}>cm</button>
                <button type="button"
                  className={`ps-cpw-pill${editUnit === "in" ? " ps-active" : ""}`}
                  onClick={() => handleUnitChange("in")}>in</button>
              </div>
              {!hasMeasurements && (
                <div className="ps-pmv-loading-pill" style={{ marginLeft: "8px" }}>
                  <span className="ps-pmv-loading-spinner" />
                  <span>{t("Calculating...")}</span>
                </div>
              )}
            </div>
          </div>

          <div className="ps-pmv-measure-list">
            {fields.map((f) => {
              const stored = (measurements as Record<string, number | undefined>)[f.key];
              const showSpinner = isEstimating && stored == null && !draft[f.key];
              return (
                <div key={f.key} className={`ps-pmv-measure-row${stored == null ? " ps-loading" : ""}`}>
                  <span className="ps-pmv-measure-icon">
                    <img src={f.iconSrc} alt="" aria-hidden="true" />
                  </span>
                  <span className="ps-pmv-measure-label">{t(f.label)}</span>
                  {/* Inline editable value — looks like text, editable on click */}
                  <span className="ps-pmv-measure-value ps-pmv-inline-edit">
                    {showSpinner ? (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: "0.4vw" }}>
                        <span className="ps-tryon-size-loading-spinner" style={{ width: "0.8vw", height: "0.8vw", borderWidth: "1.5px" }} />
                        <span style={{ color: "var(--ps-text-muted)", fontSize: "0.7vw" }}>{t("calculating...")}</span>
                      </span>
                    ) : (
                      <>
                        <input
                          type="number"
                          step="0.1"
                          className="ps-pmv-inline-input"
                          value={draft[f.key] ?? ""}
                          placeholder="—"
                          onChange={(e) => setDraft((v) => ({ ...v, [f.key]: e.target.value }))}
                        />
                        <span className="ps-pmv-inline-unit">{editUnit}</span>
                      </>
                    )}
                  </span>
                </div>
              );
            })}

            {/* Bra size rows for female profiles */}
            {profile.gender === "female" && (
              <>
                <div className="ps-pmv-measure-row">
                  <span className="ps-pmv-measure-icon" />
                  <span className="ps-pmv-measure-label">{t("BAND")}</span>
                  <span className="ps-pmv-measure-value ps-pmv-inline-edit">
                    <select
                      className="ps-pmv-inline-select"
                      value={bandSize}
                      onChange={(e) => setBandSize(e.target.value)}
                    >
                      <option value="">—</option>
                      {BAND_OPTIONS_US.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </span>
                </div>
                <div className="ps-pmv-measure-row">
                  <span className="ps-pmv-measure-icon" />
                  <span className="ps-pmv-measure-label">{t("CUP")}</span>
                  <span className="ps-pmv-measure-value ps-pmv-inline-edit">
                    <select
                      className="ps-pmv-inline-select"
                      value={cupSize}
                      onChange={(e) => setCupSize(e.target.value)}
                    >
                      <option value="">—</option>
                      {CUP_OPTIONS_US.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Actions — sticky bottom nav */}
      <div className="ps-pmv-actions">
        <button type="button" className="ps-pmv-btn-secondary" onClick={onBack}>
          {t("Back")}
        </button>
        <div className="ps-pmv-actions-right">
          {!draftMode && (
            <button type="button" className="ps-pmv-btn-delete" onClick={onDelete}>
              {t("Delete")}
            </button>
          )}
          {!draftMode && !isActive && (
            <button type="button" className="ps-pmv-btn-secondary ps-pmv-btn-use-profile" onClick={onSelect}>
              {t("USE THIS PROFILE")}
            </button>
          )}
          <button
            type="button"
            className="ps-pmv-btn-primary"
            onClick={handleSave}
          >
            {hasMeasurements ? t(primaryLabel || "SAVE") : (
              <>
                <span className="ps-pmv-btn-spinner" />
                {t("CALCULATING")}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
